import Layout from 'obvio/user/Layout'
import styled from 'styled-components'
import {useBreadcrumbs} from 'lib/ui/BreadcrumbProvider'
import Page from 'lib/ui/layout/Page'
import React, {useState} from 'react'
import {obvioRoutes} from 'obvio/Routes'
import {
  BillingInterval,
  getIntervalDetails,
  isInterval,
  isPlan,
  PlanInfo,
  useGetPlanFromQueryParams,
} from 'obvio/Billing/plans'
import {useQueryParams} from 'lib/url'
import {Redirect} from 'react-router-dom'
import Typography from '@material-ui/core/Typography'
import {formatPrice} from 'lib/currency'
import Box from '@material-ui/core/Box'
import {useDefaultPaymentMethod} from 'obvio/Billing/DefaultPaymentMethodProvider'
import ErrorAlert from 'lib/ui/alerts/ErrorAlert'
import SubscribeStep from 'obvio/Billing/ChangePlanPage/SubscribeStep'
import {useGetSubscription} from '../subscribe'
import CreditCardForm from 'obvio/Billing/CreditCardSection/DefaultCardManagement/NewCardForm'
import {formatDate, LONG_DATE_FORMAT} from 'lib/date-time'

export default function ChangePlanPage() {
  const params = useQueryParams()
  const plan = useGetPlanFromQueryParams()
  const {interval} = params

  useBreadcrumbs([
    {
      title: 'Billing',
      url: obvioRoutes.billing.root,
    },
    {
      title: 'Change Plan',
      url: `${obvioRoutes.billing.change_plan}?plan=${plan?.name}`,
    },
  ])

  if (!plan || !isPlan(plan.name)) {
    return <Redirect to={obvioRoutes.billing.root} />
  }

  if (!interval || !isInterval(interval)) {
    return <Redirect to={obvioRoutes.billing.root} />
  }

  return (
    <Content
      downgrade={'downgrade' in params}
      interval={interval}
      plan={plan}
    />
  )
}

function Content(props: {
  downgrade: boolean
  interval: BillingInterval
  plan: PlanInfo
}) {
  const {downgrade, interval, plan} = props
  const subscription = useGetSubscription()
  const [error, setError] = useState<string | null>(null)

  const [planPrice, billingInterval] = getIntervalDetails(plan, interval)
  const price = formatPrice(planPrice)
  const actionText = downgrade ? 'downgrading' : 'subscribing'

  const billedOn = () => {
    if (subscription?.renews_at) {
      return (
        <>
          on{' '}
          <strong>
            {formatDate(subscription.renews_at, LONG_DATE_FORMAT)}
          </strong>
        </>
      )
    }

    return <strong>now</strong>
  }

  return (
    <>
      <Layout>
        <Page>
          <Box textAlign="center">
            <ErrorAlert>{error}</ErrorAlert>
            <Box mb={4}>
              <Typography>
                You are {actionText} to the <strong>{plan.label}</strong> plan.
              </Typography>
              <Typography>
                You will be billed {billedOn()} for <strong>${price}</strong>,
                recurring <strong>{billingInterval}</strong> until cancelled.
              </Typography>
            </Box>
            <Step
              downgrade={downgrade}
              interval={interval}
              onError={setError}
              plan={plan}
            />
          </Box>
        </Page>
      </Layout>
    </>
  )
}

function Step(props: {
  downgrade: boolean
  interval: BillingInterval
  onError: (error: string) => void
  plan: PlanInfo
}) {
  const {paymentMethod} = useDefaultPaymentMethod()

  if (!paymentMethod) {
    return <StyledCreditCardForm disableAutoHide />
  }

  return <SubscribeStep paymentMethod={paymentMethod} {...props} />
}

const StyledCreditCardForm = styled(CreditCardForm)`
  form {
    margin: 0 auto;
  }
`
